/**
 * onboardingStore - first-run onboarding persistence, extracted from
 * onboardingFlow so pure consumers (missions gating, the boot screen) don't
 * pull in the flow's UI imports.
 *
 * localStorage `oggi_onboarding_v1` - deliberately NOT in settingsStore, so
 * "Reset all settings" does not replay onboarding. Stored shape:
 * `{ completed: true, choice, at }`. The read path validates the JSON and
 * clears corrupt values (a leftover garbage key used to suppress onboarding
 * forever), so a damaged key just replays the flow once.
 */

const STORAGE_KEY = 'oggi_onboarding_v1';

/** @returns {boolean} */
export function hasCompletedOnboarding() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.completed === true) return true;
    localStorage.removeItem(STORAGE_KEY);
    return false;
  } catch {
    // Unparseable value: clear it so it can't suppress onboarding forever.
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* private mode */
    }
    return false;
  }
}

/** @returns {string|null} the first-run choice (classic/easy/touch/follow) */
export function getOnboardingChoice() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return parsed && parsed.completed === true && typeof parsed.choice === 'string'
      ? parsed.choice
      : null;
  } catch {
    return null;
  }
}

/** @param {string} choice */
export function markCompleted(choice) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ completed: true, choice, at: new Date().toISOString() }),
    );
  } catch {
    /* private mode - onboarding will replay next visit */
  }
}
